import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

import {
    CircleAlert,
    Download,
    FileSpreadsheet,
    LoaderCircle,
    Lock,
    Upload
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { useDashboard } from '@/lib/store'
import { defineText, useText } from '@/lib/i18n/text'
import { COMMON } from '@/lib/i18n/common'
import { BrandMark } from '@/components/dashboard/brand'
import { Button } from '@/components/dashboard/button'
import { LanguageToggle, ThemeToggle } from '@/components/dashboard/preferences'
import { useFileDrop } from '@/components/dashboard/use-file-drop'

export const LOAD_TEXT = defineText({
    title: 'Tablero de Ciberseguridad',
    intro: 'Carga la base de datos en Excel para generar el tablero. El archivo se lee dentro de tu navegador y nunca sale de tu equipo.',
    drop: 'Arrastra y suelta tu archivo aquí',
    dropActive: 'Suelta el archivo para cargarlo',
    or: 'o',
    load: 'Cargar BD',
    format: 'Formato .xlsx',
    local: 'Procesamiento local, sin servidor',
    loading: 'Validando y leyendo la base…',
    error: 'No cumple con los criterios de carga.',
    sample: 'Descargar Excel de ejemplo',
    sampleBusy: 'Preparando ejemplo…',
    sampleHint: 'Datos ficticios para probar todas las vistas.'
}, {
    title: 'Cybersecurity Dashboard',
    intro: 'Load the Excel database to build the dashboard. The file is read inside your browser and never leaves your device.',
    drop: 'Drag and drop your file here',
    dropActive: 'Drop the file to load it',
    or: 'or',
    load: 'Load DB',
    format: '.xlsx format',
    local: 'Local processing, no server',
    loading: 'Validating and reading the database…',
    error: 'Does not meet the upload criteria.',
    sample: 'Download sample Excel',
    sampleBusy: 'Preparing sample…',
    sampleHint: 'Fictitious data to try every view.'
})

interface LoadScreenProps {
    onPick: () => void
}

/** The only screen shown until a valid workbook is loaded. */
export const LoadScreen: FC<LoadScreenProps> = ({ onPick }): ReactNode => {
    const text = useText(LOAD_TEXT)
    const common = useText(COMMON)
    const { status, error, loadFile } = useDashboard()
    const [sampleBusy, setSampleBusy] = useState(false)
    const alertRef = useRef<HTMLDivElement>(null)
    const loading = status === 'loading'
    const dragging = useFileDrop(file => void loadFile(file), !loading)

    useEffect(() => {
        // Warm up the dashboard code while the user picks a file
        void import('@/components/dashboard/app-shell')
    }, [])

    useEffect(() => {
        if (error) alertRef.current?.focus()
    }, [error])

    const downloadSample = async () => {
        setSampleBusy(true)

        try {
            const { downloadSampleWorkbook } = await import('@/lib/sample/workbook')

            await downloadSampleWorkbook()
        }
        finally {
            setSampleBusy(false)
        }
    }

    return (
        <div className='relative flex min-h-screen flex-col bg-background'>
            <header className='flex items-center justify-between gap-3 px-5 py-4 sm:px-8'>
                <div className='flex items-center gap-3'>
                    <BrandMark className='size-8' />

                    <div className='leading-tight'>
                        <p className='text-sm font-semibold text-foreground'>
                            Atalaya
                        </p>

                        <p className='text-xs text-muted-foreground'>
                            {common.company}
                        </p>
                    </div>
                </div>

                <div className='flex items-center gap-2'>
                    <LanguageToggle />
                    <ThemeToggle />
                </div>
            </header>

            <main id='main' className='flex flex-1 items-center justify-center px-5 pb-16 sm:px-8'>
                <div className='w-full max-w-xl'>
                    <div className='text-center'>
                        <p className='text-xs font-semibold tracking-wider text-accent-foreground uppercase'>
                            {common.directorate}
                        </p>

                        <h1 className='mt-2 text-[28px] leading-tight font-semibold tracking-tight text-balance text-foreground sm:text-[34px]'>
                            {text.title}
                        </h1>

                        <p className='mx-auto mt-3 max-w-md text-sm leading-relaxed text-pretty text-muted-foreground'>
                            {text.intro}
                        </p>
                    </div>

                    <div
                        className={cn(
                            'mt-8 rounded-card border-2 border-dashed bg-card p-8 text-center shadow-card transition-colors sm:p-10',
                            dragging ? 'border-primary bg-accent/50' : 'border-border'
                        )}
                    >
                        <span className={cn('mx-auto grid size-14 place-items-center rounded-2xl transition-colors', dragging ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground')}>
                            {loading ? <LoaderCircle className='size-6 animate-spin' aria-hidden='true' /> : <FileSpreadsheet className='size-6' aria-hidden='true' />}
                        </span>

                        {loading ? (
                            <p className='mt-4 text-sm font-medium text-foreground' role='status'>
                                {text.loading}
                            </p>
                        ) : (
                            <>
                                <p className='mt-4 text-sm font-medium text-foreground'>
                                    {dragging ? text.dropActive : text.drop}
                                </p>

                                <p className='mt-1 text-xs text-muted-foreground'>
                                    {text.or}
                                </p>

                                <Button variant='primary' className='mt-3' onClick={onPick}>
                                    <Upload className='size-4' aria-hidden='true' />
                                    {text.load}
                                </Button>
                            </>
                        )}

                        <div className='mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground'>
                            <span className='inline-flex items-center gap-1.5'>
                                <Lock className='size-3.5' aria-hidden='true' />
                                {text.local}
                            </span>

                            <span className='inline-flex items-center gap-1.5'>
                                <FileSpreadsheet className='size-3.5' aria-hidden='true' />
                                {text.format}
                            </span>
                        </div>
                    </div>

                    {error && (
                        <div
                            ref={alertRef}
                            tabIndex={-1}
                            role='alert'
                            className='mt-4 flex items-center gap-2 rounded-xl border border-rag-red/30 bg-rag-red/10 px-4 py-3 text-sm font-medium text-rag-red-ink outline-none animate-in fade-in slide-in-from-top-1'
                        >
                            <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                            {text.error}
                        </div>
                    )}

                    <div className='mt-6 flex flex-col items-center gap-1 text-center'>
                        <button
                            type='button'
                            onClick={downloadSample}
                            disabled={sampleBusy}
                            className='inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-accent-foreground outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60'
                        >
                            {sampleBusy ? <LoaderCircle className='size-3.5 animate-spin' aria-hidden='true' /> : <Download className='size-3.5' aria-hidden='true' />}
                            {sampleBusy ? text.sampleBusy : text.sample}
                        </button>

                        <span className='text-[11px] text-muted-foreground'>
                            {text.sampleHint}
                        </span>
                    </div>
                </div>
            </main>
        </div>
    )
}
